<template>
  <CommonVCard>
    <h2 class="payment-method__header pb-4">
      {{ $t("player_registration.payment_method") }}
    </h2>
    <div v-if="stripePaymentIntentDetails.clientSecret">
      <CommonPlayerProfileFormField
        :changeFormField="handleChangePaymentFormField"
        :fieldValue="cardholder"
        :label="$t('player_registration.name_on_card')"
        :placeholder="$t('player_registration.name_on_card')"
        :rules="{
          required: {
            value: 0
          },
          maxLength: {
            value: 255
          }
        }"
        isStripeForm
        name="cardholder"
      />
      <div class="payment__stripe-field-wrapper">
        <label class="payment__stripe-field-label">{{
          $t("player_registration.card_number")
        }}</label>
        <div id="card-number" class="payment__stripe-field"></div>
      </div>
      <div class="payment__stripe-field-wrapper">
        <label class="payment__stripe-field-label">{{
          $t("player_registration.exp_date")
        }}</label>
        <div id="card-expiry" class="payment__stripe-field"></div>
      </div>
      <div class="payment__stripe-field-wrapper">
        <label class="payment__stripe-field-label">{{
          $t("player_registration.security_code")
        }}</label>
        <div id="card-cvc" class="payment__stripe-field"></div>
      </div>
      <CommonPlayerProfileFormField
        :changeFormField="handleChangePaymentFormField"
        :fieldValue="zipCode"
        :label="$t('player_registration.zip_code')"
        :rules="{
          required: {
            value: 0
          },
          specificLength: {
            value: 5
          }
        }"
        :type="RegistrationAnswerType.NUMBER"
        isStripeForm
        medium
        name="zipCode"
        placeholder="12345"
      />
      <VMessages :value="[paymentErrorMessage]" color="error" />

      <p
        class="payment-method__agreement pt-6 font-weight-medium"
        v-html="renderAgreementText"
      />
    </div>
    <div class="d-flex justify-center mt-4">
      <v-container id="paypal-button-container">
        <div class="d-flex justify-center">
          <v-progress-circular
            v-if="isLoading"
            class="align-center"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
      </v-container>
      <v-btn
        id="complete-registration"
        style="display: none;"
        @click="moveToFormStep(3)"
      ></v-btn>
    </div>
    <div>
      <v-btn
        color="primary"
        elevation="1"
        large
        rounded
        width="100%"
        class="hidden"
        ref="fluidpay-submit-button"
        @click="fluidpayTokenizer.submit()"
        id="fluid-pay-submit-button"
      >
        <div v-if="!submitButtonLoading">Submit</div>
        <v-progress-circular
          v-else
          color="white"
          indeterminate
        ></v-progress-circular>
      </v-btn>
      <div
        v-if="
          playerRegistrationSummary.paymentMethod ===
            PaymentProvidersTypes.FLUIDPAY
        "
      >
        <v-container id="fluid-pay-validation-messages" />
      </div>
    </div>
  </CommonVCard>
</template>

<script>
import CommonVCard from "./CommonVCard";
import { i18n } from "@/plugins/i18n";
import { ModuleTypes } from "../../../store/ModuleTypes";
import { mapActions, mapGetters } from "vuex";
import CommonPlayerProfileFormField from "./CommonPlayerProfileFormField";
import { loadStripe } from "@stripe/stripe-js/pure";
import { RegistrationAnswerType } from "../enums/AnswerType";
import { PaymentProvidersTypes } from "../enums/PaymentProvidersTypes";

export default {
  name: "PlayerRegistrationFormPaymentMethodCard",
  components: {
    CommonPlayerProfileFormField,
    CommonVCard
  },
  props: {
    isCompleteRegistrationTriggerActive: {
      type: Boolean,
      required: true
    },
    changeDataFieldValue: {
      type: Function,
      required: true
    },
    moveToFormStep: {
      type: Function,
      required: true
    }
  },
  data: () => ({
    stripe: null,
    stripeElements: null,
    cardNumber: null,
    cardExpiry: null,
    cardCvc: null,
    cardholder: "",
    zipCode: "",
    paymentErrorMessage: "",
    RegistrationAnswerType,
    isCompleteRegistrationLoading: false,
    PaymentProvidersTypes,
    isLoading: true,
    fluidpayTokenizer: null,
    submitButton: null,
    submitButtonLoading: false
  }),
  async mounted() {
    setTimeout(() => {
      if (
        this.playerRegistrationSummary.paymentMethod !==
          PaymentProvidersTypes.PAYPAL &&
        this.playerRegistrationSummary.paymentMethod !==
          PaymentProvidersTypes.FLUIDPAY
      ) {
        this.handleNoOnlinePayment(this.playerRegistrationSummary);
      }
    }, 1000);
  },
  computed: {
    ...mapGetters(ModuleTypes.PLAYER_REGISTRATION, [
      "registrationPlayerId",
      "playerRegistrationSummary",
      "stripePaymentIntentDetails",
      "paypalCheckoutPaymentValues",
      "playerRegistration",
      "playerProfileForm"
    ]),
    playerName() {
      const firstName = this.playerProfileForm.find(
        q => q.name === "Player First Name"
      ).value;
      const lastName = this.playerProfileForm.find(
        q => q.name === "Player Last Name"
      ).value;
      return firstName + " " + lastName;
    },
    renderAgreementText() {
      return `${i18n.tc(
        "player_registration.terms_confirmation_text_1"
      )} "${i18n.tc(
        "player_registration.terms_confirmation_text_2"
      )}", ${i18n.tc(
        "player_registration.terms_confirmation_text_3"
      )} <a href="#">${i18n.tc(
        "player_registration.terms_confirmation_text_4"
      )}</a> ${i18n.tc(
        "player_registration.terms_confirmation_text_5"
      )} <a href="#">${i18n.tc(
        "player_registration.terms_confirmation_text_6"
      )}.</a>`;
    }
  },

  beforeDestroy() {
    if (this.stripePaymentIntentDetails.clientSecret) {
      this.cardNumber.destroy();
      this.cardExpiry.destroy();
      this.cardCvc.destroy();
    }
  },

  methods: {
    ...mapActions(ModuleTypes.PLAYER_REGISTRATION, [
      "finalizePaymentRegistration",
      "updatePaypalCheckoutPaymentStatus"
    ]),

    handleChangePaymentFormField(value, name) {
      this[name] = value;
    },

    async handleConfirmPaymentMethod() {
      if (
        this.playerRegistrationSummary.paymentMethod ===
        PaymentProvidersTypes.STRIPE
      ) {
        this.paymentErrorMessage = "";
        const { token, error } = await this.stripe.createToken(this.cardNumber);
        if (error) {
          this.changeDataFieldValue(
            "isCompleteRegistrationTriggerActive",
            false
          );
          this.paymentErrorMessage = error.message;
          return;
        }

        this.changeDataFieldValue("isCompleteRegistrationLoading", true);
        this.stripe
          .confirmCardPayment(this.stripePaymentIntentDetails?.clientSecret, {
            payment_method: {
              card: {
                token: token.id
              },
              billing_details: {
                name: this.cardholder,
                address: {
                  postal_code: this.zipCode
                }
              }
            }
          })
          .then(async result => {
            if (!result?.error) {
              await this.finalizePaymentRegistration({
                successCallback: () => this.moveToFormStep(3)
              });
              this.changeDataFieldValue("isCompleteRegistrationLoading", false);
            } else {
              this.changeDataFieldValue(
                "isCompleteRegistrationTriggerActive",
                false
              );
              this.changeDataFieldValue("isCompleteRegistrationLoading", false);
              this.paymentErrorMessage = result.error.message;
            }
          });
      }
    },

    async loadPaypalScript() {
      const paymentScript = document.getElementById("paymentScript");
      if (!paymentScript) {
        alert("Error loading PayPal");
        console.error("Error loading PayPal script: Script element not found.");
        return;
      }

      const { VUE_APP_PAYPAL_BASE_URL, VUE_APP_PAYPAL_CLIENT_ID } = process.env;
      const { paymentAccountEmail } = this.playerRegistrationSummary;
      const { paymentAccountMerchantId } = this.playerRegistrationSummary;
      console.log("Player reg summary...");
      console.log(JSON.stringify(this.playerRegistrationSummary));
      console.log(JSON.stringify(process.env, null, 2));
      console.log("Starting to load PayPal script...");

      return new Promise((resolve, reject) => {
        // Set event handlers before assigning the src
        paymentScript.onload = () => {
          console.log("PayPal script loaded successfully.");
          resolve();
        };

        paymentScript.onerror = () => {
          console.error("Failed to load PayPal script.");
          reject(new Error("Failed to load PayPal script"));
        };

        // Now set the src, after event handlers are ready
        paymentScript.src = `${VUE_APP_PAYPAL_BASE_URL}/sdk/js?client-id=${VUE_APP_PAYPAL_CLIENT_ID}&merchant-id=${paymentAccountMerchantId}&currency=USD`;
        paymentScript.dataset.sdkIntegrationSource = "button-factory";
        paymentScript.dataset.pageType = "checkout";

        console.log("PayPal script source set:", paymentScript.src);
      });
    },

    async loadFluidPayScript() {
      const paymentScript = document.getElementById("paymentScript");
      if (!paymentScript) {
        alert("Error loading FluidPay");
        console.error("Error loading FluidPay script.");
        return;
      }

      const { VUE_APP_FLUIDPAY_TOKENIZER_URL } = process.env;

      return new Promise((resolve, reject) => {
        // Set event handlers before assigning the src
        paymentScript.onload = () => {
          paymentScript.dataset.loaded = "true"; // Set the custom attribute to mark as loaded
          resolve();
        };

        paymentScript.onerror = () => {
          reject(new Error("Failed to load FluidPay script"));
        };

        // Now set the src, after event handlers are ready
        paymentScript.src = VUE_APP_FLUIDPAY_TOKENIZER_URL;
      });
    },

    async handleFluidPayPayment(
      playerRegistrationSummary,
      playerRegistrationId,
      playerName,
      existingFee,
      prevResponse
    ) {
      this.submitButtonLoading = false;
      const container = document.getElementById("paypal-button-container");
      container.innerHTML = "";
      const submissionCallback = async response => {
        this.submitButtonLoading = true;
        const container = document.getElementById("paypal-button-container");
        const messageContainer = document.getElementById(
          "fluid-pay-validation-messages"
        );
        container.innerHTML = "";
        this.submitButton.$el.classList.add("hidden");
        let playerFeeReq;
        let fee = null;
        let paymentReq;
        let paymentRes;

        messageContainer.innerHTML = "";

        if (existingFee) {
          fee = existingFee;
        }

        switch (response.status) {
          case "success":
            if (!response.user || !response.billing) {
              await this.handleFluidPayPayment(
                playerRegistrationSummary,
                playerRegistrationId,
                playerName,
                fee,
                response
              );
            }

            if (!response.user.first_name || !response.user.last_name) {
              await this.handleFluidPayPayment(
                playerRegistrationSummary,
                playerRegistrationId,
                playerName,
                fee,
                response
              );
            }

            if (
              !response.billing.address ||
              !response.billing.city ||
              !response.billing.state ||
              !response.billing.zip ||
              !response.billing.country
            ) {
              await this.handleFluidPayPayment(
                playerRegistrationSummary,
                playerRegistrationId,
                playerName,
                fee,
                response
              );
            }

            paymentReq = {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
              },
              method: "POST",
              body: JSON.stringify({
                apiKey: playerRegistrationSummary.fluidPayApiKey,
                paymentAccountId: playerRegistrationSummary.paymentAccountId,
                token: response,
                description:
                  playerRegistrationSummary.registrationName +
                  " [" +
                  playerName +
                  "]",
                playerRegistration: {
                  id: playerRegistrationSummary.id,
                  plan: playerRegistrationSummary.userPickedPaymentPlan,
                  total: playerRegistrationSummary.userPickedPaymentPlan
                    ? playerRegistrationSummary.installmentInfoList[0]
                        .fullAmountIntCents
                    : playerRegistrationSummary.total
                }
              })
            };

            paymentRes = await fetch(
              "/checkout/processSaleFromTokenizer",
              paymentReq
            ).then(res => res.json());

            if (paymentRes.success) {
              if (paymentRes.response.data.response === "approved") {
                playerFeeReq = {
                  headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                  },
                  method: "POST",
                  body: `${JSON.stringify(paymentRes.response.data)}`
                };

                const fee = await fetch(
                  `/rest-api/registration/second-step/${playerRegistrationId}/create-player-fee`,
                  playerFeeReq
                ).then(res => res.json());

                const finalizeRegistration = await fetch(
                  `/rest-api/registration/second-step/${playerRegistrationId}/finalize`,
                  { method: "POST" }
                ).then(res => res.status === 200);

                if (finalizeRegistration) {
                  if (container) {
                    container.innerHTML =
                      "<h3> Thank you for your payment. </h3>";
                  }
                  setTimeout(
                    () =>
                      document.getElementById("complete-registration").click(),
                    2000
                  );
                } else {
                  if (container) {
                    container.innerHTML =
                      "<h3>Your payment has been received, but your player could not be added to this registration. " +
                      "Please email <a href='mailto:support@goteambot.com'>support@goteambot.com</a>" +
                      " to resolve this issue. </h3>";
                  }
                }
              } else if (paymentRes.response.data.response === "declined") {
                messageContainer.innerHTML =
                  "\n" +
                  "<h1 style='color:red;'> " +
                  "Your payment method was declined. " +
                  "Please try again." +
                  `</h1>`;
                container ? (container.innerHTML = "") : null;
                this.submitButton.$el.classList.add("hidden");
                await this.handleFluidPayPayment(
                  playerRegistrationSummary,
                  playerRegistrationId,
                  playerName,
                  fee,
                  response
                );
              }
            } else {
              container.innerHTML = "";
              container.innerHTML =
                "<h3>Your payment could not be processed. \n " +
                "Please contact <a href='mailto:support@goteambot.com'>TeamBot Support</a>." +
                "</h3>";
            }

            this.submitButtonLoading = false;
            this.submitButton.$el.classList.add("hidden");
            break;

          case "validation":
            container ? (container.innerHTML = "") : null;
            this.submitButton.$el.classList.add("hidden");
            await this.handleFluidPayPayment(
              playerRegistrationSummary,
              playerRegistrationId,
              playerName,
              fee,
              response
            );
            break;

          case "error":
            messageContainer.innerHTML =
              "<p>An unexpected error has occurred. Please try again. " +
              " If issues persist, please contact <a href='mailto:support@goteambot.com'>TeamBot Support</a>.</p>";
            container ? (container.innerHTML = "") : null;
            this.submitButton.$el.classList.add("hidden");
            await this.handleFluidPayPayment(
              playerRegistrationSummary,
              playerRegistrationId,
              playerName,
              fee,
              response
            );
            break;

          default:
            break;
        }
        this.isLoading = false;
      };

      const paymentChangeCallback = type => {
        if (this.submitButton) {
          if (type === "ach") {
            this.submitButton.$el.classList.remove("hidden");
          }
        }
      };

      const validCardCallback = () => {
        if (this.submitButton) {
          this.submitButton.$el.classList.remove("hidden");
        }
      };

      const messageContainer = document.getElementById(
        "fluid-pay-validation-messages"
      );

      if (prevResponse && prevResponse.status === "validation") {
        messageContainer.innerHTML = "";
        prevResponse.invalid.forEach(field => {
          let inputError = field.split(".");
          inputError = inputError[inputError.length - 1];
          messageContainer.innerHTML +=
            "\n" +
            "<p style='color:red; font-size: 18px'> " +
            `* ${i18n.tc("payments.fluidpay.validation." + inputError)}</p>`;
        });
      }

      this.fluidpayTokenizer = new Tokenizer({
        apikey: playerRegistrationSummary.fluidPayApiKey,
        container: "#paypal-button-container",
        submission: async resp => await submissionCallback(resp),
        onPaymentChange: type => paymentChangeCallback(type),
        validCard: card => validCardCallback(),
        settings: {
          payment: {
            types: ["card", "ach"],
            ach: {
              user_required: {
                first_name: true,
                last_name: true
              },
              billing_required: {
                address: true,
                city: true,
                state: true,
                zip: true,
                country: true
              }
            },
            card: {
              user_required: {
                first_name: true,
                last_name: true
              },
              billing_required: {
                address: true,
                city: true,
                state: true,
                zip: true,
                country: true
              }
            }
          },
          user: {
            showInline: true,
            showName: true
          },
          billing: {
            show: true
          }
        }
      });
      this.isLoading = false;
    },

    handlePaypalPayment(
      playerRegistrationSummary,
      playerRegistrationId,
      playerName
    ) {
      this.isLoading = true;
      const element = document.getElementById("paypal-button-container");
      if (element) element.innerHTML = "";
      window.paypal
        .Buttons({
          style: {
            layout: "vertical",
            color: "blue",
            shape: "pill",
            label: "paypal",
            height: 55
          },
          createOrder: async function(data, actions) {
            const order = await fetch(
              `/paypal/createPaypalOrderForRegistration`,
              {
                method: "POST",
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json"
                },
                body: JSON.stringify({
                  payPalEmail: `${playerRegistrationSummary.paymentAccountEmail}`,
                  amount: playerRegistrationSummary.userPickedPaymentPlan
                    ? playerRegistrationSummary.installmentInfoList[0]
                        .fullAmountIntCents / 100
                    : playerRegistrationSummary.total / 100,
                  description:
                    playerName +
                    " - " +
                    playerRegistrationSummary.registrationName,
                  playerRegistrationId: playerRegistrationId
                })
              }
            ).then(res => {
              return res.json();
            });
            return order.payPalOrder.id;
          },
          onApprove: async function(data, actions) {
            const req = {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json"
              },
              body: JSON.stringify(data),
              method: "POST"
            };

            const fee = await fetch(
              `/rest-api/registration/second-step/${playerRegistrationId}/create-player-fee`,
              req
            ).then(res => res.json());

            const response = await fetch(
              `/paypal/capturePaypalPayment/${fee.id}?orderId=` +
                data.orderID +
                `&playerRegistrationId=${playerRegistrationId}`,
              req
            ).then(res => res.json());

            if (response.success) {
              const finalizeRegistration = await fetch(
                `/rest-api/registration/second-step/${playerRegistrationId}/finalize`,
                { method: "POST" }
              ).then(res => res.status === 200);

              if (finalizeRegistration) {
                const element = document.getElementById(
                  "paypal-button-container"
                );
                if (element) {
                  element.innerHTML = "<h3> Thank you for your payment. </h3>";
                }
                setTimeout(
                  () =>
                    document.getElementById("complete-registration").click(),
                  2000
                );
              } else {
                const element = document.getElementById(
                  "paypal-button-container"
                );
                if (element) {
                  element.innerHTML =
                    "<h3>Your payment has been received, but your player could not be added to this registration. " +
                    "Please email <a href='mailto:support@goteambot.com'>support@goteambot.com</a>" +
                    " to resolve this issue. </h3>";
                }
              }
            } else if (
              Array.isArray(response.capturePayment) &&
              response.capturePayment.details[0].issue === "INSTRUMENT_DECLINED"
            ) {
              return actions.restart(); // if the payment method is declined, this restarts the paypal checkout process as per https://developer.paypal.com/demo/checkout/#/pattern/server
            } else {
              const element = document.getElementById(
                "paypal-button-container"
              );
              if (element) {
                element.innerHTML =
                  "<h3>Your Payment Could Not Be Processed. </h3>";
              }
            }
          },
          onError: function(err) {
            console.log(err);
          }
        })
        .render("#paypal-button-container");
      this.isLoading = false;
    },
    async handleNoOnlinePayment(playerRegistrationSummary) {
      if (playerRegistrationSummary.subTotal > 0) {
        const element = document.getElementById("paypal-button-container");
        element.innerHTML =
          "<h3>Online payment unavailable.</h3>" +
          "<h3>Click save. Then contact your coach to pay.</h3>";
      } else {
        const element = document.getElementById("paypal-button-container");
        element.innerHTML =
          "<h3>There is no fee associated with this registration</h3>" +
          "<h3>Please Click Save and Complete Registration Below</h3>";
      }
    }
  },

  watch: {
    isCompleteRegistrationTriggerActive: function(newValue) {
      if (newValue) {
        this.handleConfirmPaymentMethod();
      }
    },
    "playerRegistrationSummary.userPickedPaymentPlan": {
      handler: async function(newValue, oldValue) {
        const element = document.getElementById("paypal-button-container");
        /*
         * PAYPAL
         */
        if (
          this.playerRegistrationSummary.paymentMethod ===
          PaymentProvidersTypes.PAYPAL
        ) {
          if (!window.paypal) {
            try {
              await this.loadPaypalScript();
            } catch (error) {
              // todo: handle error (e.g., show a user notification)
              alert("Error loading PayPal.");
              console.error("Error loading PayPal.");
              console.error(error.message);
              return;
            }
          }

          element.innerHTML = "";
          this.handlePaypalPayment(
            this.playerRegistrationSummary,
            this.playerRegistrationSummary.id,
            this.playerRegistration.name
          );
        }

        /*
         * FLUID PAY
         */
        if (
          this.playerRegistrationSummary.paymentMethod ===
          PaymentProvidersTypes.FLUIDPAY
        ) {
          this.submitButton = this.$refs["fluidpay-submit-button"];

          if (!window.Tokenizer) {
            try {
              await this.loadFluidPayScript();
            } catch (error) {
              // todo: handle error (e.g., show a user notification)
              alert("Error loading Payment Gateway.");
              console.error("Error loading Payment Gateway.");
              console.error(error.message);
              return;
            }
          }

          await this.handleFluidPayPayment(
            this.playerRegistrationSummary,
            this.playerRegistrationSummary.id,
            this.playerName
          );
        }
      },
      immediate: true
    },
    "playerRegistrationSummary.total": {
      handler: async function(newValue, oldValue) {
        if (newValue && newValue !== 0) {
          const element = document.getElementById("paypal-button-container");
          if (
            this.playerRegistrationSummary.paymentMethod ===
            PaymentProvidersTypes.PAYPAL
          ) {
            if (!window.paypal) {
              try {
                await this.loadPaypalScript();
              } catch (error) {
                // todo: handle error (e.g., show a user notification)
                alert("Error loading PayPal.");
                console.error("Error loading PayPal.");
                console.error(error.message);
                return;
              }
            }

            element.innerHTML = "";
            this.handlePaypalPayment(
              this.playerRegistrationSummary,
              this.playerRegistrationSummary.id,
              this.playerRegistration.name
            );
          } else if (
            this.playerRegistrationSummary.paymentMethod ===
            PaymentProvidersTypes.FLUIDPAY
          ) {
            this.submitButton = this.$refs["fluidpay-submit-button"];

            if (!window.Tokenizer) {
              try {
                await this.loadFluidPayScript();
              } catch (error) {
                // todo: handle error (e.g., show a user notification)
                alert("Error loading PayPal.");
                console.error("Error loading PayPal.");
                console.error(error.message);
                return;
              }
            }

            element.innerHTML = "";
            await this.handleFluidPayPayment(
              this.playerRegistrationSummary,
              this.playerRegistrationSummary.id,
              this.playerName
            );
          } else if (newValue === 0) {
            const element = document.getElementById("paypal-button-container");
            if (element) {
              element.innerHTML =
                "<h3>There is no amount due. Please click save to finish.</h3>";
            }
          }
        }
      },
      immediate: true,
      deep: true
    },
    stripePaymentIntentDetails: async function(newValue) {
      const { clientSecret, stripeAccountId } = newValue;
      if (clientSecret && stripeAccountId) {
        this.stripe = await loadStripe(
          "pk_test_51JcAZKLj3eHzofp7mpv6rA7NeADKU0Je8zyYvRsTcQ4YO6R79vIBsXmT1EFWXWiCzJClXEZSpT5tiuW1CZoDaCnq00QMGdnPMT",
          {
            stripeAccount: stripeAccountId
          }
        );
        this.stripeElements = await this.stripe.elements();
        const style = {
          base: {
            padding: "10px",
            color: "#000",
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: "antialiased",
            fontSize: "14px",
            "::placeholder": {
              color: "#9e9e9e",
              fontSize: "15px",
              fontWeight: "300"
            }
          },
          invalid: {
            color: "#fa755a",
            iconColor: "#fa755a"
          }
        };
        if (this.stripeElements) {
          this.cardNumber = this.stripeElements.create("cardNumber", {
            style
          });
          this.cardNumber.mount("#card-number");
          this.cardExpiry = this.stripeElements.create("cardExpiry", {
            style
          });
          this.cardExpiry.mount("#card-expiry");
          this.cardCvc = this.stripeElements.create("cardCvc", { style });
          this.cardCvc.mount("#card-cvc");
        }
      }
    }
  }
};
</script>

<style>
#paypal-button-container > h3 {
  text-align: center;
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.47);
}
</style>
<style scoped>
.hidden {
  display: none;
}

.payment-method__header {
  font-size: 18px;
  font-weight: 500;
  color: var(--dark-color);
}

.payment-method__agreement {
  border-top: 1px solid var(--divider-color);
}

.payment__stripe-field-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.payment__stripe-field-label {
  flex-basis: 25%;
  margin-right: 12px;
  color: #9e9e9e;
}

.payment__stripe-field {
  flex-grow: 1;
  display: flex;
  align-items: center;
  border-radius: 10px;
  background-color: var(--content-bg-color);
  height: 40px;
  padding: 12px;
  border: 1px solid var(--divider-color);
}

.payment__stripe-field >>> .__PrivateStripeElement {
  width: 100%;
}

.payment__stripe-field::placeholder {
  color: red !important;
}

.payment__stripe-field#card-expiry,
.payment__stripe-field#card-cvc {
  flex-grow: initial;
  width: 200px !important;
}
</style>
